package ru.villawonka.app.presentation.design.theme

import androidx.compose.material.Colors
import androidx.compose.material.darkColors
import androidx.compose.material.lightColors
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.colorspace.ColorSpaces

object AppColors {

    val transparent = Color.Transparent

    val grayLight50 = Color(0xFFFFFFFF)
    val grayLight100 = Color(0xFFF4F7FA)
    val grayLight200 = Color(0xFFE6EBEE)
    val grayLight300 = Color(0xFFD2DCE0)
    val grayLight400 = Color(0xFFB8C5CD)
    val grayLight500 = Color(0xFF90A3AF)
    val grayLight600 = Color(0xFF8395A4)
    val grayLight700 = Color(0xFF708191)
    val grayLight800 = Color(0xFF5E6C79)
    val grayLight900 = Color(0xFF4E5964)
    val grayLight950 = Color(0xFF051627)

    val grayDark50 = Color(0xFFF5F5F6)
    val grayDark100 = Color(0xFFE5E6E8)
    val grayDark200 = Color(0xFFCDCED4)
    val grayDark300 = Color(0xFFABADB5)
    val grayDark400 = Color(0xFF808390)
    val grayDark500 = Color(0xFF656775)
    val grayDark600 = Color(0xFF575863)
    val grayDark700 = Color(0xFF414149)
    val grayDark800 = Color(0xFF2A2D32)
    val grayDark900 = Color(0xFF1E2024)
    val grayDark950 = Color(0xFF080809)

    val violet50 = Color(0xFFFFFFFF)
    val violet100 = Color(0xFFF4E8FF)
    val violet200 = Color(0xFFEAD6FE)
    val violet300 = Color(0xFFDAB5FD)
    val violet400 = Color(0xFFC386FA)
    val violet500 = Color(0xFFAC57F5)
    val violet600 = Color(0xFF9836E7)
    val violet700 = Color(0xFF8826D4)
    val violet800 = Color(0xFF6F23A6)
    val violet900 = Color(0xFF5B1E85)
    val violet950 = Color(0xFF3E0863)

    val blue50 = Color(0xFFEFF8FF)
    val blue100 = Color(0xFFDAEFFF)
    val blue200 = Color(0xFFBEE3FF)
    val blue300 = Color(0xFF91D3FF)
    val blue400 = Color(0xFF5CB9FE)
    val blue500 = Color(0xFF2B93FA)
    val blue600 = Color(0xFF207BF0)
    val blue700 = Color(0xFF1964DC)
    val blue800 = Color(0xFF1B51B2)
    val blue900 = Color(0xFF1C478C)
    val blue950 = Color(0xFF162C55)

    val birch50 = Color(0xFFF2F9FD)
    val birch100 = Color(0xFFE4F2FA)
    val birch200 = Color(0xFFC4E4F3)
    val birch300 = Color(0xFF90CFE9)
    val birch400 = Color(0xFF41AED8)
    val birch500 = Color(0xFF2E9EC9)
    val birch600 = Color(0xFF1F7FAA)
    val birch700 = Color(0xFF1A658A)
    val birch800 = Color(0xFF195673)
    val birch900 = Color(0xFF1A4860)
    val birch950 = Color(0xFF122E3F)

    val pink50 = Color(0xFFFFFFFF)
    val pink100 = Color(0xFFFFE1E5)
    val pink200 = Color(0xFFFFC7D2)
    val pink300 = Color(0xFFFF99AE)
    val pink400 = Color(0xFFFF6084)
    val pink500 = Color(0xFFFF295E)
    val pink600 = Color(0xFFFA0D51)
    val pink700 = Color(0xFFD3003F)
    val pink800 = Color(0xFFB0013D)
    val pink900 = Color(0xFF96053C)
    val pink950 = Color(0xFF55001B)

    val red50 = Color(0xFFFEF2F2)
    val red100 = Color(0xFFFEE2E2)
    val red200 = Color(0xFFFFC9C9)
    val red300 = Color(0xFFFDA4A4)
    val red400 = Color(0xFFF95C5C)
    val red500 = Color(0xFFF14242)
    val red600 = Color(0xFFDE2424)
    val red700 = Color(0xFFBB1A1A)
    val red800 = Color(0xFFBB1A1A)
    val red900 = Color(0xFF801C1C)
    val red950 = Color(0xFF460909)


}

fun AppColors.getColorScheme(isDarkTheme: Boolean): Colors =
    if (isDarkTheme) {
        darkColors(
            primary = violet700,
            primaryVariant = violet800,
            secondary = blue500,
            secondaryVariant = blue600,
            surface = grayDark950,
            error = red950,
            background = grayDark950,
            onPrimary = grayDark50,
            onSecondary = grayDark50,
            onSurface = grayDark50,
            onError = grayDark50,
            onBackground = grayDark50,
        )
    } else {
        lightColors(
            primary = violet700,
            primaryVariant = violet800,
            secondary = blue500,
            secondaryVariant = blue600,
            surface = grayLight50,
            error = red100,
            background = grayLight100,
            onPrimary = grayLight50,
            onSecondary = grayLight50,
            onSurface = grayLight950,
            onError = grayLight50,
            onBackground = grayLight950,
        )
    }

/**
 * Errors
 */

val Colors.errorContentInverted
    get() = if (isLight) AppColors.red400 else AppColors.red500

val Colors.errorBackgroundEnabled
    get() = if (isLight) AppColors.red100 else AppColors.red950

val Colors.errorBackgroundHovered
    get() = if (isLight) AppColors.red200 else AppColors.red900

val Colors.errorBackgroundFocused
    get() = if (isLight) AppColors.red200 else AppColors.red900

val Colors.errorOutlineEnabled
    get() = if (isLight) AppColors.red400 else AppColors.red600

val Colors.errorOutlineHovered
    get() = if (isLight) AppColors.red400 else AppColors.red600

val Colors.errorOutlineFocused
    get() = if (isLight) AppColors.red400 else AppColors.red600

/**
 * Surface
 */

val Colors.surfaceLand
    get() = if (isLight) AppColors.grayLight100 else AppColors.grayDark800

val Colors.surfaceTop
    get() = if (isLight) AppColors.grayLight50 else AppColors.grayDark900

val Colors.surfaceMiddle
    get() = if (isLight) AppColors.grayLight50 else AppColors.grayDark900

// Костыль для js-target'a, к сожалению,
// по другому пока никак не вытащить цвет для передачи в js-код Telegram WebApp'a
val Colors.surfaceMiddleHexString
    get() = if (isLight) "#FFFFFF" else "#1E2024"

val Colors.surfaceLow
    get() = if (isLight) AppColors.grayLight100 else AppColors.grayDark950

/**
 * On surface
 */

val Colors.onSurfacePrimary
    get() = if (isLight) AppColors.grayLight950 else AppColors.grayDark50

val Colors.onSurfaceSecondary
    get() = if (isLight) AppColors.grayLight500 else AppColors.grayDark200

val Colors.onSurfaceInverse
    get() = if (isLight) AppColors.grayLight50 else AppColors.grayDark950

/**
 * Overlay
 */

val Colors.overlay
    get() = if (isLight) Color(0f, 0f, 0f, 0.4f) else Color(0f, 0f ,0f , 0.6f)

/**
 * Outline
 */

val Colors.outlinePrimary
    get() = if (isLight) AppColors.grayLight200 else AppColors.grayDark600

/**
 * Buttons
 */

val Colors.filledButtonSecondaryContentInverted
    get() = if (isLight) AppColors.blue500 else AppColors.blue500

val Colors.filledButtonSecondaryBackgroundEnabled
    get() = if (isLight) AppColors.blue500 else AppColors.blue500

/**
 * Colors
 */

val Colors.colorsContentEnabled
    get() = if (isLight) AppColors.grayLight50 else AppColors.grayDark50

val Colors.colorsBackgroundColor1
    get() = AppColors.violet700

val Colors.colorsBackgroundColor2
    get() = AppColors.blue500

val Colors.colorsBackgroundColor3
    get() = AppColors.birch400

val Colors.colorsBackgroundColor4
    get() = AppColors.pink500

val Colors.colorsBackgroundColor5
    get() = AppColors.grayDark400